import type { JSX, MouseEvent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  Hovercard,
  HovercardAnchor,
  HovercardHeading,
  useHovercardState,
} from 'ariakit/hovercard'
import { Anchor, csx, Paragraph, Stack, Tooltip } from '@vtex/admin-ui'
import { useAdmin } from '@vtex/raccoon-next'
import { resolveLocale } from '@/lib/resolveLocale'

import { IconInactive, IconPending, IconPublished } from './icons'

import * as styles from './products-listing.styles'

export function StatusIcon(props: StatusProps) {
  const { pending, published, Icon } = props

  const { formatMessage } = useIntl()
  const { locale = '' } = useAdmin()
  const hovercard = useHovercardState({ gutter: 4, placement: 'top' })

  const resolvedLocale = resolveLocale(locale)

  const helpCenterLinks = {
    pt: 'https://help.vtex.com/pt/faq/por-que-o-produto-nao-aparece-no-site',
    es: 'https://help.vtex.com/es/faq/por-que-el-producto-no-aparece-en-el-sitio-web',
    en: 'https://help.vtex.com/faq/why-is-the-product-not-visible-on-the-website',
  }

  const helpCenterLink =
    helpCenterLinks[resolvedLocale as keyof typeof helpCenterLinks] ||
    helpCenterLinks.pt

  function handlePropagation<T>(e: MouseEvent<T>) {
    return e.stopPropagation()
  }

  return pending ? (
    <>
      <HovercardAnchor
        state={hovercard}
        href={helpCenterLink}
        target="_blank"
        onClick={handlePropagation}
        className={styles.iconContainer}
      >
        <Icon />
      </HovercardAnchor>
      <Hovercard
        className={styles.hovercard}
        state={hovercard}
        onClick={handlePropagation}
      >
        <Stack space="$space-2">
          <Stack direction="row">
            <Icon />
            <HovercardHeading className={styles.hovercardHeader}>
              {formatMessage({
                id: 'admin/admin.catalog.pending-status-card-heading',
                defaultMessage: 'Unavailable product',
              })}
            </HovercardHeading>
          </Stack>
          <Paragraph className={csx(styles.hovercardDescription)}>
            <FormattedMessage
              id="admin/admin.catalog.pending-status-card-description"
              defaultMessage="Check if this product has all the <b>required information</b>, <b>prices</b>, <b>inventory</b>, and <b>logistics settings</b>."
              values={{ b: (str) => <span>{str}</span> }}
            />
          </Paragraph>
          <Anchor
            href={helpCenterLink}
            target="_blank"
            onClick={handlePropagation}
          >
            {formatMessage({
              id: 'admin/admin.catalog.pending-status-card-link',
              defaultMessage: 'Learn more',
            })}
          </Anchor>
        </Stack>
      </Hovercard>
    </>
  ) : (
    <Tooltip
      placement="top"
      text={
        published
          ? formatMessage({
              id: 'admin/admin.catalog.published-status-description',
              defaultMessage: 'Available for sale',
            })
          : formatMessage({
              id: 'admin/admin.catalog.inactive-status-description',
              defaultMessage: 'Inactive in the catalog',
            })
      }
    >
      <span className={styles.iconContainer}>
        <Icon />
      </span>
    </Tooltip>
  )
}

interface UseStatusProps {
  active: boolean
  visibleInPortal: boolean
}

interface StatusProps {
  pending: boolean
  published: boolean
  inactive: boolean
  label: string
  Icon: () => JSX.Element
}

export function useStatusIcon(props: UseStatusProps): StatusProps {
  const { active, visibleInPortal } = props

  if (active && !visibleInPortal)
    return {
      Icon: IconPending,
      label: 'admin/admin.catalog.pending-status-label',
      pending: true,
      published: false,
      inactive: false,
    }

  if (!active)
    return {
      Icon: IconInactive,
      label: 'admin/admin.catalog.inactive-status-label',
      pending: false,
      published: false,
      inactive: true,
    }

  return {
    Icon: IconPublished,
    label: 'admin/admin.catalog.published-status-label',
    pending: false,
    published: true,
    inactive: false,
  }
}
